import React, { useEffect } from "react";
import { Helmet } from "react-helmet";

import {
  buildOrganizationSchema,
  buildWebSiteSchema,
  DEFAULT_OG_IMAGE,
  SITE_NAME,
  toAbsoluteUrl,
  toCanonicalUrl,
  truncateText,
} from "./seoUtils";

const PageSeo = ({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = "website",
  robots = "index,follow",
  structuredData = [],
  articlePublishedTime,
  articleModifiedTime,
}) => {
  const resolvedTitle = title || SITE_NAME;
  const resolvedDescription = truncateText(description || "", 160);
  const resolvedCanonical = toCanonicalUrl(canonical);
  const resolvedOgTitle = ogTitle || resolvedTitle;
  const resolvedOgDescription = truncateText(
    ogDescription || resolvedDescription,
    200
  );
  const resolvedOgImage = toAbsoluteUrl(ogImage || DEFAULT_OG_IMAGE);
  const structuredDataItems = Array.isArray(structuredData)
    ? structuredData
    : [structuredData];
  const resolvedStructuredDataItems = [
    buildOrganizationSchema(),
    buildWebSiteSchema(),
    ...structuredDataItems,
  ]
    .filter(Boolean)
    .filter((schema, index, schemas) => {
      const key = schema["@id"] || `${schema["@type"] || "Thing"}:${schema.name || schema.url || index}`;

      return schemas.findIndex((item, itemIndex) => {
        const itemKey = item["@id"] || `${item["@type"] || "Thing"}:${item.name || item.url || itemIndex}`;
        return itemKey === key;
      }) === index;
    });

  useEffect(() => {
    let canonicalLink = document.querySelector('link[rel="canonical"]');

    if (!canonicalLink) {
      canonicalLink = document.createElement("link");
      canonicalLink.rel = "canonical";
      document.head.appendChild(canonicalLink);
    }

    canonicalLink.setAttribute("href", resolvedCanonical);
  }, [resolvedCanonical]);

  return (
    <Helmet>
      <title>{resolvedTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <meta name="robots" content={robots} />
      <link rel="alternate" hrefLang="en-IN" href={resolvedCanonical} />

      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={resolvedOgTitle} />
      <meta property="og:description" content={resolvedOgDescription} />
      <meta property="og:url" content={resolvedCanonical} />
      <meta property="og:image" content={resolvedOgImage} />
      {ogType === "article" && articlePublishedTime && (
        <meta property="article:published_time" content={articlePublishedTime} />
      )}
      {ogType === "article" && articleModifiedTime && (
        <meta property="article:modified_time" content={articleModifiedTime} />
      )}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={resolvedOgTitle} />
      <meta name="twitter:description" content={resolvedOgDescription} />
      <meta name="twitter:image" content={resolvedOgImage} />

      {resolvedStructuredDataItems
        .map((schema, index) => (
          <script
            key={`structured-data-${index}`}
            type="application/ld+json"
          >
            {JSON.stringify(schema)}
          </script>
        ))}
    </Helmet>
  );
};

export default PageSeo;
